import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs, { tabsClasses } from '@mui/material/Tabs';
import { Theme, styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQueryClient } from '@tanstack/react-query';
import { JSX, ReactNode, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';

import { userKeys } from 'api/queries';
import { Namespace } from 'i18next';
import { Area } from 'library/models/Area';
import { textOverflowSx } from 'library/utils/themes/PCTheme';
import { hasUserAccess } from 'main/auth/Authorization';

import { DraggableProps } from '../../dnd/Draggable';
import { DraggableTabs } from './DraggableTabs';
import { TabLabel } from './TabLabel';
import { VerticalTabs } from './VerticalTabs';
import { NewBadge } from './components/NewBadge';
import { ScrollableTabsProps, TabConfiguration } from './types';

const TabsStyled = styled(Tabs)(({ theme }) => ({
	background: theme.palette.background.default,
	padding: theme.spacing(1, 0),
	'& .MuiTabs-list > *': {
		margin: theme.spacing(0, 0.5),
	},
	'& .MuiTab-root': {
		minWidth: 110,
		background: theme.palette.background.paper,
	},
	[`& .${tabsClasses.scrollButtons}`]: {
		'&.Mui-disabled': { opacity: 0.2 },
	},
	'& .MuiTabItem-labelGroup': {
		minWidth: 0,
	},
	'& .MuiTabItem-subLabel': {
		fontSize: '0.65rem',
		textAlign: 'left',
		fontWeight: 'normal',
		...textOverflowSx,
	},
	'& .MuiTabItem-tag': {
		position: 'absolute',
		top: 5,
		right: 5,
		fontSize: '0.75rem',
		borderRadius: 12,
		lineHeight: theme.spacing(2),
		padding: '1px 6px',
		letterSpacing: 0.3,
		whiteSpace: 'nowrap',
	},
	'& .MuiTabItem-tag-error': {
		color: theme.palette.error.contrastText,
		background: theme.palette.error.main,
	},
	'& .MuiTabItem-tag-info': {
		color: theme.palette.info.contrastText,
		background: theme.palette.text.primary,
	},
}));

export const ScrollableTabs = memo(
	({
		tabs: tabsConfiguration,
		paddingStyle,
		tabsSettingType,
		onTabClick,
	}: ScrollableTabsProps) => {
		const { t } = useTranslation<Namespace>();

		const isMobile = useMediaQuery((theme: Theme) =>
			theme.breakpoints.only('xs')
		);

		// might be worth switching to search params
		const { pathname } = useLocation();
		const activeTab = tabsConfiguration.find(({ path }) =>
			pathname.includes(path)
		);
		const rootRef = useRef<HTMLElement | null>(null);

		const queryClient = useQueryClient();

		const availableAreas = queryClient.getQueryData<Area[]>(
			userKeys.securityMatrix()
		);

		const visibleTabs = tabsConfiguration
			.filter(
				(item) =>
					item.areaSecurity === undefined ||
					hasUserAccess(item.areaSecurity, availableAreas ?? [])
			)
			.map((tab) => ({
				...tab,
				title: t(tab.title),
			})) as TabConfiguration[];

		const setScrollPosition = () => {
			if (!rootRef.current) {
				return;
			}

			const boxRect = rootRef.current.getBoundingClientRect();

			if (window.innerHeight - boxRect.top < 200) {
				window.scrollBy({
					top: 250,
					behavior: 'smooth',
				});
			}
		};

		const sx = paddingStyle === 'tight' ? {} : { py: 3, px: 3 };

		const renderTabs = <T, P>(
			renderedTabs: (TabConfiguration & T)[],
			component: (props: P) => JSX.Element | ReactNode
		) => (
			<TabsStyled
				value={activeTab?.path || false}
				variant="scrollable"
				scrollButtons
				aria-label="visible arrows tabs"
				selectionFollowsFocus>
				{renderedTabs.map(
					({
						badge,
						icon,
						path,
						subtitle,
						title,
						endDateNewBadge,
						areaSecurity,
						badgeLevel,
						hasDivider,
						pageTitle,
						...rest
					}) => (
						<Tab
							sx={[
								sx,
								{ textWrap: 'nowrap' },
								badge && paddingStyle !== 'tight'
									? { pr: 5 }
									: null,
							]}
							key={path}
							component={component}
							to={path}
							value={path}
							onClick={() => {
								setScrollPosition();
								onTabClick?.(path);
							}}
							label={
								<>
									<NewBadge
										endDateNewBadge={endDateNewBadge}
										sx={{
											position: 'absolute',
											top: 0,
											right: 0,
											'& > .MuiChip-label': {
												padding: 0.5,
											},
										}}
									/>
									<TabLabel
										badge={badge}
										subtitle={subtitle}
										title={title}
									/>
								</>
							}
							iconPosition="top"
							icon={icon}
							{...rest}
						/>
					)
				)}
			</TabsStyled>
		);

		return (
			<>
				{!isMobile && (
					<Box
						data-testid="desktop-scrollable-tabs"
						ref={rootRef}
						sx={(theme) => ({
							display: 'grid',
							mt: 4,
							mb: 3,
							[theme.breakpoints.only('xs')]: {
								mt: 0,
								mx: 0,
							},
						})}>
						{tabsSettingType ? (
							<DraggableTabs
								tabsSettingType={tabsSettingType}
								renderTabs={
									renderTabs<
										DraggableProps,
										NavLinkProps & DraggableProps
									>
								}
								tabs={visibleTabs}
							/>
						) : (
							renderTabs(visibleTabs, NavLink)
						)}
					</Box>
				)}
				<VerticalTabs
					visibleTabs={visibleTabs}
					activeTab={activeTab}
					onTabClick={onTabClick}
				/>
			</>
		);
	}
);
